/**
 * The phone Knowledge Archive's header - everything above the graph.
 *
 *   Knowledge Archive        [Graph | List]
 *   TOP 300 OF 1,247 · Show all
 *   [ Search concepts…        ] [Filters 2]
 *   (algebra ×) (this week ×) →
 *
 * - **The count is the cap line.** Desktop has a count in the header AND a
 *   cap banner under it; at 390 the two said the same number twice, with the
 *   count wrapped onto two lines. Here one mono line carries the number and,
 *   when capped, the way to the rest.
 * - **Filters sit beside the search** they narrow with, labelled and counted.
 *   It was an unlabelled icon up by Back, with nothing to say a filter was on.
 * - **The active filters show as a chip row**, each one removable, so a
 *   filtered map never passes for the whole archive.
 *
 * Owns the filter sheet: the trigger is here, so the sheet is too. Its footer
 * pairs "Clear all" with "Show N concepts" - filters apply live, and the
 * button is the way back to the map that says what the map will show.
 */

import { useState } from 'react';
import { List, Network } from 'lucide-react';
import { SegmentedControl } from '@ai-tutor/ui';
import { ConceptSearchInput } from '@/components/features/mindspace/concepts/ConceptSearchInput';
import { MindspaceFilterPanel } from '@/components/features/mindspace/filters/MindspaceFilterPanel';
import { ActiveFilterChips } from '@/components/features/mindspace/filters/ActiveFilterChips';
import {
  FiltersButton,
  PhoneFilterSheet,
} from '@/components/features/mindspace/filters/PhoneFilters';
import type { ArchiveView } from '@/hooks/mindspace/useArchiveView';

const VIEWS = [
  { value: 'graph', label: 'Graph', icon: Network },
  { value: 'list', label: 'List', icon: List },
] as const;

export interface ArchiveCount {
  /** Concepts on screen. */
  visible: number;
  /** All the user has, or `null` before the response lands. */
  total: number | null;
  /** The server capped the graph. */
  capped: boolean;
  /** The user lifted the cap. */
  showingAll: boolean;
  defaultCap: number;
  onShowAll: () => void;
  onRestoreCap: () => void;
}

interface ArchivePhoneHeaderProps {
  count: ArchiveCount;
  view: ArchiveView;
  onViewChange: (view: ArchiveView) => void;
}

export function ArchivePhoneHeader({
  count,
  view,
  onViewChange,
}: ArchivePhoneHeaderProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <header className='flex-shrink-0 border-b border-border px-4 pb-2 pt-2'>
      <div className='flex items-center justify-between gap-3'>
        <h2 className='min-w-0 text-xl font-semibold leading-tight text-foreground'>
          Knowledge Archive
        </h2>
        <SegmentedControl
          label='Show concepts as'
          options={VIEWS}
          value={view}
          onChange={onViewChange}
          size='sm'
          className='shrink-0'
          segmentClassName='tap-target'
        />
      </div>
      <CountLine {...count} />

      <div className='mt-2 flex gap-2'>
        <div className='min-w-0 flex-1'>
          <ConceptSearchInput />
        </div>
        <FiltersButton
          open={filtersOpen}
          onClick={() => setFiltersOpen(true)}
        />
      </div>

      <ActiveFilterChips showPausedChip variant='row' />

      <PhoneFilterSheet
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        doneLabel={showLabel(count)}
      >
        <MindspaceFilterPanel layout='sheet' />
      </PhoneFilterSheet>
    </header>
  );
}

function countText({
  visible,
  total,
  capped,
  showingAll,
}: Pick<ArchiveCount, 'visible' | 'total' | 'capped' | 'showingAll'>) {
  // A non-breaking space, not nothing, so the line keeps its height and the
  // search below it does not jump when the count lands.
  if (visible === 0 && total == null) return '\u00a0';
  const all = total?.toLocaleString() ?? '?';
  if (capped && showingAll) return `All ${all} concepts`;
  if (capped) return `Top ${visible.toLocaleString()} of ${all}`;
  return `${visible.toLocaleString()} concept${visible === 1 ? '' : 's'}`;
}

/** The sheet's way back, saying what the map will show. */
function showLabel({ visible, total }: ArchiveCount) {
  if (total == null && visible === 0) return 'Show concepts';
  return `Show ${visible.toLocaleString()} concept${visible === 1 ? '' : 's'}`;
}

/** "10 CONCEPTS", or capped "TOP 300 OF 1,247 · Show all". */
function CountLine({
  visible,
  total,
  capped,
  showingAll,
  defaultCap,
  onShowAll,
  onRestoreCap,
}: ArchiveCount) {
  const text = countText({ visible, total, capped, showingAll });
  return (
    <p
      data-testid='archive-count'
      className='mt-0.5 flex h-5 items-center gap-2 font-mono text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground'
    >
      <span>{text}</span>
      {capped && (
        <>
          <span aria-hidden='true'>·</span>
          <button
            type='button'
            onClick={showingAll ? onRestoreCap : onShowAll}
            // `z-10`: its 44px tap area reaches into the gap above the
            // search box, and has to sit over it to take the finger.
            className='tap-target z-10 font-sans text-sm font-semibold normal-case tracking-normal text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            {showingAll ? `Top ${defaultCap}` : 'Show all'}
          </button>
        </>
      )}
    </p>
  );
}
